// File: properties/UpdateChannelConsentProperties.jsx
import React from 'react';
import {
  Box,
  VStack,
  HStack,
  Text,
  Input,
  Select,
  Button,
  Divider,
  Alert,
  AlertIcon,
  AlertDescription,
  Flex,
} from '@chakra-ui/react';
import { FiSlash } from 'react-icons/fi';
import { FaCheckCircle } from 'react-icons/fa';
import useConsentChannels from '../utils/useConsentChannels';

const CONSENT_ACTIONS = [
  {
    value: 'enable',
    label: 'Enable',
    hint: 'Opt-In',
    colorScheme: 'green',
    icon: FaCheckCircle,
  },
  {
    value: 'disable',
    label: 'Disable',
    hint: 'Opt-Out',
    colorScheme: 'red',
    icon: FiSlash,
  },
];

export default function UpdateChannelConsentProperties({
  nodeConfig,
  setNodeConfig,
  updateNodeData,
}) {
  const { channels, loading: channelsLoading } = useConsentChannels();

  // The label is stored with the id so the node on the canvas can render the channel
  // name without re-fetching the (now dynamic) list.
  const handleChannelChange = (id) => {
    const picked = channels.find((c) => String(c.id) === String(id));
    setNodeConfig({ ...nodeConfig, channel: id, channelLabel: picked?.label || '' });
  };

  return (
    <VStack spacing={4} align="stretch">
      {/* Step Name */}
      <Box>
        <Text fontSize="xs" fontWeight="semibold" color="gray.600" mb={1.5}>
          Step Name
        </Text>
        <Input
          size="sm"
          value={nodeConfig.label || ''}
          onChange={(e) => setNodeConfig({ ...nodeConfig, label: e.target.value })}
          placeholder="e.g., Enable SMS Opt-In"
        />
      </Box>

      <Divider />

      {/* Channel */}
      <Box>
        <Text fontSize="xs" fontWeight="semibold" color="gray.600" mb={1.5}>
          Channel
        </Text>
        <Select
          size="sm"
          value={nodeConfig.channel || ''}
          onChange={(e) => handleChannelChange(e.target.value)}
          placeholder={channelsLoading ? 'Loading channels…' : 'Select channel'}
          isDisabled={channelsLoading}
        >
          {channels.map((channel) => (
            <option key={channel.id} value={channel.id}>
              {channel.label}
            </option>
          ))}
        </Select>
      </Box>

      <Divider />

      {/* Consent Action */}
      <Box>
        <Text fontSize="xs" fontWeight="semibold" color="gray.600" mb={1.5}>
          Consent Action
        </Text>
        <HStack spacing={2} align="stretch">
          {CONSENT_ACTIONS.map((action) => {
            const isSelected = nodeConfig.consentAction === action.value;
            const ActionIcon = action.icon;
            return (
              <Box
                key={action.value}
                as="button"
                type="button"
                flex={1}
                p={3}
                borderWidth="2px"
                borderRadius="lg"
                borderColor={isSelected ? `${action.colorScheme}.400` : 'gray.200'}
                bg={isSelected ? `${action.colorScheme}.50` : 'white'}
                _hover={{ borderColor: `${action.colorScheme}.300` }}
                onClick={() =>
                  setNodeConfig({ ...nodeConfig, consentAction: action.value })
                }
              >
                <Flex direction="column" align="center" gap={1}>
                  <Box color={isSelected ? `${action.colorScheme}.500` : 'gray.400'}>
                    <ActionIcon size={18} />
                  </Box>
                  <Text
                    fontSize="xs"
                    fontWeight="semibold"
                    color={isSelected ? `${action.colorScheme}.700` : 'gray.500'}
                  >
                    {action.label}
                  </Text>
                  <Text
                    fontSize="10px"
                    color={isSelected ? `${action.colorScheme}.500` : 'gray.400'}
                  >
                    {action.hint}
                  </Text>
                </Flex>
              </Box>
            );
          })}
        </HStack>
      </Box>

      {/* Info */}
      <Alert status="info" borderRadius="md" py={2} px={3}>
        <AlertIcon boxSize="14px" />
        <AlertDescription fontSize="xs">
          Updates the contact's opt-in preference for the chosen channel.{' '}
          <strong>Enable</strong> marks them as opted in; <strong>Disable</strong> marks
          them as opted out and prevents future messages on that channel.
        </AlertDescription>
      </Alert>

      {/* Save */}
      <Button
        size="sm"
        w="full"
        bg="#C026D3"
        color="white"
        _hover={{ bg: '#A21CAF' }}
        onClick={updateNodeData}
      >
        Update Node
      </Button>
    </VStack>
  );
}
